import { useState, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';

export const useMultiSelect = <T extends { id: string }>(
  items: T[],
  onDelete: (ids: string[]) => Promise<void>
) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const toggleMultiSelect = useCallback(() => {
    setIsMultiSelectMode(prev => !prev);
    if (isMultiSelectMode) {
      setSelectedItems(new Set());
    }
  }, [isMultiSelectMode]);

  const toggleItemSelection = useCallback((id: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedItems(new Set(items.map(item => item.id)));
  }, [items]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedItems.size === 0) return;

    try {
      await onDelete(Array.from(selectedItems));
      toast({
        title: "Success",
        description: `Successfully deleted ${selectedItems.size} item(s)`,
      });
      setSelectedItems(new Set());
      setIsMultiSelectMode(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete selected items",
        variant: "destructive",
      });
    }
  }, [selectedItems, onDelete]);

  const clearSelection = useCallback(() => {
    setSelectedItems(new Set());
    setIsMultiSelectMode(false);
  }, []);

  const isSelected = useCallback((id: string) => selectedItems.has(id), [selectedItems]);

  return {
    selectedItems,
    isMultiSelectMode,
    toggleMultiSelect,
    toggleItemSelection,
    handleDeleteSelected,
    clearSelection,
    isSelected,
    selectedCount: selectedItems.size,
    selectAll
  };
}; 